import cn from "../../utils/cn"
import { useVideoScanSummary } from "../../hooks/useVideoLogs"
import {
  buildPlaybackIndex,
  getVideoProgress,
  summariseVideos,
} from "./videoMetrics"

/*
  One video and how far the patient got with it.

  The bar restates the percentage printed next to it, so it is hidden from
  assistive tech rather than given a progressbar role — visible text beats an
  invisible ARIA value for everyone.

  The percentage sits where the design originally showed a timestamp. The
  scan-summary endpoint returns no per-video time; if it ever gains a
  `LastPlaybackAt`, it belongs in this same slot beside the percentage.
*/
const VideoRow = ({ video, playback, isLast }) => {
  const {
    isViewed,
    label,
    barPercent,
    isComplete,
    watchCount,
    completedPlayCount,
  } = getVideoProgress(playback)

  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 py-2",
        !isLast && "border-b border-gray-200"
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            "truncate text-[13px]",
            isViewed ? "font-medium text-gray-800" : "text-gray-500"
          )}
          title={video?.VideoTitle}
        >
          {video?.VideoTitle || `Video ${video?.VideoId}`}
        </span>

        {watchCount > 0 && (
          <span
            className="shrink-0 rounded-full bg-blue-50 px-1.5 text-[10px] text-blue-700"
            title={`${watchCount} ${watchCount === 1 ? "play" : "plays"} (${completedPlayCount} finished). A new play starts after finishing, or after jumping back to the start once past 90%.`}
          >
            Played {watchCount}×
          </span>
        )}
      </div>

      <span
        className={cn(
          "shrink-0 text-[11px] tabular-nums",
          isViewed ? "text-gray-500" : "italic text-gray-400"
        )}
      >
        {isViewed ? label : "not viewed"}
      </span>

      <div
        aria-hidden="true"
        className="col-span-2 h-[3px] overflow-hidden rounded-full bg-gray-200"
      >
        <div
          className={cn(
            "h-full rounded-full",
            isComplete ? "bg-green-500" : "bg-secondary"
          )}
          style={{ width: `${barPercent}%` }}
        />
      </div>
    </div>
  )
}

/*
  The report's videos with their playback engagement.

  Shares the ["videoScanSummary", uuid] query with ScanEngagementCard and
  FeedbackSummaryCard, so all three together still cost one request — as long as
  each is handed the same uuid value rather than deriving its own. A row without
  a UUID leaves the query disabled and simply lists the videos.
*/
const ReportVideosCard = ({ uuid, videos }) => {
  const { data } = useVideoScanSummary(uuid)

  const playbackByVideoId = buildPlaybackIndex(data?.playbackSummary)
  const { total, viewedCount, averagePercent } = summariseVideos(
    videos,
    playbackByVideoId
  )

  return (
    <div className="rounded-xl bg-gray-50 border-gray-100 p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Report videos
        </p>

        {total > 0 && (
          <span
            className="rounded-md bg-white px-2 py-0.5 text-[11px] text-gray-600"
            title={
              viewedCount === 0
                ? "None of these videos has been played yet"
                : averagePercent === null
                  ? "Played, but stopped before the first 25% milestone"
                  : `Average completion of the ${viewedCount} video${viewedCount === 1 ? "" : "s"} that ${viewedCount === 1 ? "was" : "were"} played`
            }
          >
            {viewedCount} of {total} viewed
          </span>
        )}
      </div>

      {total > 0 ? (
        <div>
          {videos.map((video, index) => (
            <VideoRow
              key={video?.VideoId ?? index}
              isLast={index === videos.length - 1}
              playback={playbackByVideoId[String(video?.VideoId)]}
              video={video}
            />
          ))}
        </div>
      ) : (
        <p className="py-1 text-xs italic text-gray-400">No report videos</p>
      )}
    </div>
  )
}

export default ReportVideosCard
